import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Modal, Switch, Radio, Alert } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { getIssuancesApi, reviewRejectionApi } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import { itemRender, defaultPagination, pageSizeOptions, handlePageChange } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { rejectionReasonOptions } from '../../constants';
import PageHeader from 'components/PageHeader';
import useStickyIdentity from '../../components/useStickyIdentity';
import ReportFilterBar, { cleanParams } from '../../reports/ReportFilterBar';
import { UNIFORM_LANE } from '../../components/columns';

const REVIEW_STATUS_OPTIONS = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Denied', value: 'DENIED' },
];
/** The page opens on what needs a decision. Clear shows everything. */
const DEFAULT_FILTERS = { review_status: 'PENDING' };
const FIELDS = ['search', 'reviewStatus'];
/** The filter fields that live in the URL. */
const URL_FIELDS = ['review_status', 'search'];

/** Read the applied filters out of the query string.
 *
 *  The filters live in the URL, not in component state alone, because
 *  reviewing a rejection means leaving this page — "View" opens the issuance —
 *  and coming back remounted the component, resetting the filter to Pending
 *  and losing the list the reviewer had set up. In the URL they survive the
 *  round trip, browser Back, a refresh, and a pasted link.
 *
 *  An absent `?` means a first visit, which opens on Pending. An explicit
 *  `?review_status=` (what Clear writes) means "all statuses" and must NOT
 *  fall back to the default, so presence is tested on the parameter, not on
 *  its value.
 */
const filtersFromSearch = (params) => {
  if (!URL_FIELDS.some((f) => params.has(f))) return DEFAULT_FILTERS;
  const out = {};
  URL_FIELDS.forEach((f) => {
    const v = params.get(f);
    if (v) out[f] = v;
  });
  return out;
};

const reasonLabel = (value) => rejectionReasonOptions.find((r) => r.value === value)?.label || value || '-';

/** What happened to the stock, in words — null while nothing has. */
const stockOutcome = (record) => {
  if (record.rejection_review_status !== 'APPROVED') return null;
  if (record.rejection_credit_stock === false) return { text: 'Not credited — damaged or lost', tone: '#B54708' };
  if (record.rejection_return_warehouse_name) return { text: `Returned to ${record.rejection_return_warehouse_name}`, tone: '#067647' };
  return { text: 'Credited back', tone: '#067647' };
};

const RejectionReview = () => {
  const navigate = useNavigate();
  const { warehouseOptions, warehousesLoading } = useUniformMgmt();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  // Seeded from the URL so a round trip to an issuance and back keeps the
  // list the reviewer set up. Read once: the URL is written by applyFilters,
  // never read back into state, so typing in the box cannot fight the router.
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialFilters] = useState(() => filtersFromSearch(searchParams));
  const [draft, setDraft] = useState(initialFilters);
  const [applied, setApplied] = useState(initialFilters);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedIssuance, setSelectedIssuance] = useState(null);
  const [reviewAction, setReviewAction] = useState('APPROVED');
  const [returnWarehouse, setReturnWarehouse] = useState(null);
  const [creditStock, setCreditStock] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);

  const fetchData = useCallback(() => {
    let ignore = false;
    setLoading(true);
    getIssuancesApi({
      has_rejection: true,
      ...cleanParams(applied),
      page: pageSettings.page,
      page_size: pageSettings.pageSize
    }).then(response => {
      if (ignore) return;
      setData(response.data?.results || []);
      setPageSettings(prev => ({ ...prev, total: response.data?.count || 0 }));
    }).catch(() => {
      if (ignore) return;
      toast.error('Failed to load rejections', { position: "top-right", autoClose: 3000, transition: Bounce });
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [applied, pageSettings.page, pageSettings.pageSize]);

  useEffect(() => fetchData(), [fetchData]);

  const applyFilters = (next) => {
    setApplied(next);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
    // `replace` so Back leaves the page rather than stepping through every
    // filter the reviewer tried. An empty value is still written as an empty
    // parameter, which is what tells a later mount "all statuses, on
    // purpose" instead of re-applying the Pending default.
    const params = {};
    URL_FIELDS.forEach((f) => { params[f] = next[f] || ''; });
    setSearchParams(params, { replace: true });
  };

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const openReview = (record) => {
    setSelectedIssuance(record);
    setReviewAction('APPROVED');
    setReturnWarehouse(null);
    setCreditStock(true);
    setShowReviewModal(true);
  };

  const handleReview = () => {
    const rejectionId = selectedIssuance?.rejection_id;
    if (!rejectionId) {
      toast.error('No rejection record found for this issuance', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }

    setReviewLoading(true);
    reviewRejectionApi(rejectionId, {
      review_status: reviewAction,
      return_to_warehouse: reviewAction === 'APPROVED' && returnWarehouse ? returnWarehouse.value : null,
      credit_stock: reviewAction === 'APPROVED' ? creditStock : true
    }).then(() => {
      toast.success(`Rejection ${reviewAction.toLowerCase()}`, { position: "top-right", autoClose: 3000, transition: Bounce });
      setReviewLoading(false);
      setShowReviewModal(false);
      setSelectedIssuance(null);
      fetchData();
    }).catch(e => {
      setReviewLoading(false);
      const data = e.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        data?.non_field_errors?.[0] ||
        (data && typeof data === 'object'
          ? Object.entries(data)
              .filter(([k]) => k !== 'status')
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join(' | ')
          : null) ||
        'Review failed';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const pinLeft = useStickyIdentity();

  const columns = [
    {
      // Pinned: whose rejection this is, kept on screen while the reviewer
      // scrolls to the reason and the action.
      title: 'Employee', ...pinLeft(200),
      render: (_, record) => (
        <div>
          <span>{record.company_user_name}</span>
          <br /><small className="text-muted">{record.company_user_emp_no}</small>
        </div>
      )
    },
    // Pinned beside Employee: the reviewer scrolls right to the reason,
    // the stock decision and the action, and needs to keep both 'who' and
    // 'which uniform' on screen while they do.
    { title: 'Uniform', dataIndex: 'uniform_item_name',
      ...pinLeft(UNIFORM_LANE, { secondary: true }), ellipsis: { showTitle: true } },
    {
      title: 'Size', dataIndex: 'size_detail',
      render: (detail) => detail ? `${detail.code}` : '-'
    },
    { title: 'Warehouse', dataIndex: 'warehouse_name' },
    { title: 'Issued Date', dataIndex: 'issuance_date', render: (d) => formatDateHandler(d) },
    {
      title: 'Reason', dataIndex: 'rejection_reason',
      render: (reason, record) => (
        <span title={record.rejection_reason_detail || undefined}>
          {reasonLabel(reason)}
          {record.rejection_reason_detail && <i className="fa fa-comment-o ms-1 text-muted" aria-label="Has a note" />}
        </span>
      )
    },
    {
      title: 'Review Status',
      render: (_, record) => {
        const rs = record.rejection_review_status || 'PENDING';
        const color = rs === 'PENDING' ? '#faad14' : rs === 'APPROVED' ? '#52c41a' : '#ff4d4f';
        return <span style={{ color, fontWeight: 600 }}>{rs}</span>;
      }
    },
    {
      // The decision that was taken on the stock, which the reviewer could
      // set but never see again. Blank until there is one.
      title: 'Stock',
      render: (_, record) => {
        const outcome = stockOutcome(record);
        return outcome ? <span style={{ color: outcome.tone, fontWeight: 500 }}>{outcome.text}</span> : <span className="text-muted">–</span>;
      }
    },
    {
      title: 'Reviewed',
      render: (_, record) => record.rejection_reviewed_at ? (
        <div>
          <span>{record.rejection_reviewed_by_name || '-'}</span>
          <br /><small className="text-muted">{formatDateHandler(record.rejection_reviewed_at)}</small>
        </div>
      ) : <span className="text-muted">–</span>
    },
    {
      title: 'Action',
      render: (_, record) => {
        const reviewed = record.rejection_review_status && record.rejection_review_status !== 'PENDING';
        return (
          <div className="d-flex gap-2">
            <Button
              label={reviewed ? 'Reviewed' : 'Review'}
              className={`btn btn-sm ${reviewed ? 'btn-outline-secondary' : 'btn-primary'}`}
              disabled={reviewed}
              onClick={() => openReview(record)}
            />
            <Button label="View" icon="fa fa-eye" className="btn btn-sm btn-outline-primary"
              onClick={() => navigate(`/uniform-management/issuance/${record.id}`)} />
          </div>
        );
      }
    }
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader title="Rejection Review" />

        <ReportFilterBar
          fields={FIELDS}
          value={draft} appliedValue={applied} onChange={setDraft}
          searchPlaceholder="Employee name or emp no"
          extraSelects={[{ field: 'reviewStatus', key: 'review_status', label: 'Review Status', options: REVIEW_STATUS_OPTIONS, placeholder: 'All statuses' }]}
          onApply={() => applyFilters(draft)}
          onClear={() => { setDraft({}); applyFilters({}); }}
        />

        <Table columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
          pagination={{
            total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
            pageSizeOptions, itemRender, showSizeChanger: true
          }}
          onChange={handleChange} scroll={{ x: "max-content" }} />

        <Modal
          title="Review Rejection"
          open={showReviewModal}
          onCancel={() => setShowReviewModal(false)}
          width={520}
          centered
          destroyOnClose
          footer={[
            <button key="cancel" type="button" className="btn btn-light"
              onClick={() => setShowReviewModal(false)} disabled={reviewLoading}>
              Cancel
            </button>,
            <Button
              key="submit"
              label={reviewAction === 'APPROVED' ? 'Approve Rejection' : 'Deny Rejection'}
              className={`btn ${reviewAction === 'APPROVED' ? 'btn-success' : 'btn-danger'} ms-2`}
              loading={reviewLoading}
              onClick={handleReview}
            />
          ]}
        >
          {selectedIssuance && (
            <div>
              <div className="p-3 mb-3" style={{
                background: '#F9FAFB', borderRadius: 10, border: '1px solid #F1F2F4'
              }}>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted" style={{ fontSize: 12 }}>Employee</span>
                  <span className="fw-semibold">{selectedIssuance.company_user_name}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted" style={{ fontSize: 12 }}>Uniform</span>
                  <span className="fw-semibold">{selectedIssuance.uniform_item_name}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted" style={{ fontSize: 12 }}>Size</span>
                  <span className="fw-semibold">{selectedIssuance.size_detail?.code || '—'}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted" style={{ fontSize: 12 }}>Reason given</span>
                  <span className="fw-semibold">{reasonLabel(selectedIssuance.rejection_reason)}</span>
                </div>
                {selectedIssuance.rejection_reason_detail && (
                  <div className="mt-2 p-2" style={{ background: '#fff', borderRadius: 8, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    {selectedIssuance.rejection_reason_detail}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">Decision</label>
                <Radio.Group
                  value={reviewAction}
                  onChange={(e) => setReviewAction(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  style={{ width: '100%' }}
                >
                  <Radio.Button value="APPROVED" style={{ width: '50%', textAlign: 'center' }}>
                    <i className="fa fa-check me-1" />Approve
                  </Radio.Button>
                  <Radio.Button value="DENIED" style={{ width: '50%', textAlign: 'center' }}>
                    <i className="fa fa-times me-1" />Deny
                  </Radio.Button>
                </Radio.Group>
              </div>

              {reviewAction === 'APPROVED' && (
                <>
                  <div className="mb-3 d-flex align-items-center justify-content-between p-3"
                    style={{ background: '#F9FAFB', borderRadius: 10 }}>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: 14 }}>Credit stock back?</div>
                      <small className="text-muted">
                        {creditStock ? 'Stock will be returned to warehouse' : 'Item is damaged or lost'}
                      </small>
                    </div>
                    <Switch checked={creditStock} onChange={setCreditStock} />
                  </div>
                  {creditStock && (
                    <div className="mb-2">
                      <label className="col-form-label">
                        Return to Warehouse <small className="text-muted">(optional override)</small>
                      </label>
                      <FilterSelect isClearable onChange={setReturnWarehouse} optionLabel="label" optionValue="value"
                        options={warehouseOptions} value={returnWarehouse}
                        placeholder="Use original warehouse" isLoading={warehousesLoading} />
                    </div>
                  )}
                </>
              )}

              {reviewAction === 'DENIED' && (
                <Alert
                  type="warning"
                  showIcon
                  message="The issuance status will revert to Accepted and the employee will be notified."
                  style={{ borderRadius: 10 }}
                />
              )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default RejectionReview;
